import React from "react";
import { Table, TableBody, TableCell, TableHead, TableRow, IconButton } from "@mui/material";
import { DashCircleFill } from "react-bootstrap-icons";
import { useSelector } from "react-redux";
import Description from "../Inputs/Description";
import Amount from "../Inputs/Amount";
import { BudgetItem, BudgetType, CurrentUser, DateTime, FamilyBucketLabel, SubBudgetItem, TransactionItem } from "../../types";

interface BulkSubBudgetEditorProps {
  budgetName: string;
  budgetAmount: string;
  budgetIdx: number;
  row: BudgetItem;
  transactions: TransactionItem[];
  checkRequests: TransactionItem[];
  handleSubBudgetChange: (budgetIdx: number, budget: BudgetItem) => void;
}

const BulkSubBudgetEditor: React.FC<BulkSubBudgetEditorProps> = ({
  budgetName,
  budgetAmount,
  budgetIdx,
  row,
  transactions,
  checkRequests,
  handleSubBudgetChange
}) => {
  const user: CurrentUser = useSelector((state: any) => state.userData.value);
  const subBudgetLabel = FamilyBucketLabel(user, "subBudget", "SubBudget");
  if (!transactions) return <></>;
  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell sx={{ fontWeight: 'bold' }}>{budgetName}: {budgetAmount}</TableCell>
        </TableRow>
        <TableRow>
          <TableCell className="compact-table-cell header">Name</TableCell>
          <TableCell className="compact-table-cell header">Spending {subBudgetLabel}</TableCell>
          <TableCell className="compact-table-cell header">Income {subBudgetLabel}</TableCell>
          <TableCell className="compact-table-cell bottomless" style={{ width: 32 }}></TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {[...(row.subBudgets ?? []), createEmptySubBudget(row)].map((sub, subIdx, arr) => (
          <TableRow key={`sub-${budgetIdx}-${subIdx}`} sx={{ backgroundColor: subIdx % 2 === 1 ? '#ddffff' : 'inherit' }}>
            <TableCell className="compact-table-cell">
              <Description
                NoLabel
                NoMB
                Variant="standard"
                Value={sub.description}
                OnChange={(val: React.ChangeEvent<HTMLInputElement>) => handleChange(subIdx, "description", val.target.value)}
                Size={12}
              />
            </TableCell>
            <TableCell className="compact-table-cell">
              <Amount
                NoLabel
                NoMB
                Variant="standard"
                Size={12}
                Value={sub.variant?.amount ?? 0}
                Disabled={row.budgetType === BudgetType.Income}
                OnChange={(val: React.ChangeEvent<HTMLInputElement>) => handleChange(subIdx, "variant", { ...sub.variant, amount: parseFloat(val.target.value) })}
              />
            </TableCell>
            <TableCell className="compact-table-cell">
              <Amount
                NoLabel
                NoMB
                Variant="standard"
                Size={12}
                Value={sub.variant?.incomeAmount ?? 0}
                Disabled={row.budgetType === BudgetType.Expense}
                OnChange={(val: React.ChangeEvent<HTMLInputElement>) => handleChange(subIdx, "variant", { ...sub.variant, incomeAmount: parseFloat(val.target.value) })}
              />
            </TableCell>
            <TableCell className="compact-table-cell bottomless" style={{ width: 32, backgroundColor: 'azure' }}>
              {(subIdx !== arr.length - 1) && (
                <IconButton 
                  size="small" 
                  color="error" 
                  tabIndex={-1} 
                  disabled={transactions.filter(t => t.allocations.some(c => c.subBudgetId === sub.subBudgetId)).length > 0 || checkRequests?.filter(t => t.allocations.some(c => c.subBudgetId === sub.subBudgetId)).length > 0} 
                  onClick={() => handleDeleteSubBudget(budgetIdx, subIdx)}>
                  <DashCircleFill />
                </IconButton>
              )}
            </TableCell>
          </TableRow>
        ))}
        <TableRow><TableCell className="bottomless"></TableCell></TableRow>
      </TableBody>
    </Table>
  );

  function handleDeleteSubBudget(budgetIdx: number, subIdx: number) {
    const subBudgets = (row.subBudgets ?? []).filter((_, j) => j !== subIdx);
    handleSubBudgetChange(budgetIdx, { ...row, subBudgets });
  }
  function handleChange(subIdx: number, field: string, value: any) {
    let subBudgets = row.subBudgets ? row.subBudgets.map((sub, j) =>
      j === subIdx ? { ...sub, [field]: value } : sub
    ) : [];
    // If editing the last (blank) subbudget row, add a new blank row
    if (subIdx === (subBudgets.length) && !isSubBudgetEmpty(subBudgets[subIdx])) {
      subBudgets = [...subBudgets, createEmptySubBudget(row)];
    }
    // If editing the blank row, update it
    if (subIdx === subBudgets.length) {
      subBudgets[subIdx] = { ...subBudgets[subIdx], [field]: value };
    }
    handleSubBudgetChange(budgetIdx, { ...row, subBudgets });
  }
  function createEmptySubBudget(row: BudgetItem) {
    return {
      subBudgetId: 0,
      budgetId: row.budgetId,
      description: '',
      isInactive: false,
      variant: {
        subBudgetVariantId: 0,
        subBudgetId: 0,
        budgetVariantId: row.variant?.budgetVariantId ?? 0,
        amount: 0,
    incomeAmount: 0,
        createdBy: 0,
        createdDate: new DateTime(),
        modifiedBy: 0,
        modifiedDate: new DateTime(),
      },
      amountSpent: 0,
      amountRemaining: 0,
    };
  }
  function isSubBudgetEmpty(subBudget: SubBudgetItem) {
    return !subBudget?.description && !(subBudget?.variant?.amount) && !(subBudget?.variant?.incomeAmount);
  }
};

export default BulkSubBudgetEditor;
